<template>
    <div class="w-full pb-20 bg-gray-50 flex flex-col justify-center items-center">
        <div
            class="flex items-end justify-center w-full text-xs sticky shadow top-toolbar bg-white p-4 z-10"
            v-if="references.length > 1"
        >
            <div class="flex w-full items-end justify-start gap-4 max-w-5xl px-5 bg-white">
                <app-select v-model="referenceId" :label="$t('dashboard.reference')" @input="saveReferenceChoice">
                    <option v-for="reference in references" :value="reference.id">
                        {{ reference.createdAt | humanizeDate }} - {{ reference.name }}
                    </option>
                </app-select>
            </div>
        </div>
        <div class="flex flex-col gap-5 w-full max-w-5xl px-4">
            <div class="w-full text-xs mt-4">
                <dashboard-burn-down v-if="project_EXE" :referenceId="referenceId" />
            </div>

            <app-dashboard-tiles :title="$t('dashboard.bundlesProgress')" chartClasses="">
                <template v-slot:icon>
                    <div class="p-3 rounded-full border-2 text-gray-800">
                        <icon-clock-outline width="24px" height="24px" />
                    </div>
                </template>
                <template v-slot:chart>
                    <div class="">
                        <div class="flex gap-2 items-center justify-center min-w-full">
                            <div class="gap-1 flex justify-start items-center">
                                <div class="w-6 rounded-full h-2" style="background-color: rgb(247, 76, 20)"></div>
                                <span class="text-2xs">{{ $t('dashboard.referenceProgress') }}</span>
                            </div>
                            <div class="gap-1 flex justify-start items-center">
                                <div class="w-6 rounded-full h-2" style="background-color: #509cec"></div>
                                <span class="text-2xs">{{ $t('dashboard.plannedProgress') }}</span>
                            </div>
                            <div class="gap-1 flex justify-start items-center">
                                <div class="w-6 rounded-full h-2" style="background-color: #2a4365"></div>
                                <span class="text-2xs">{{ $t('dashboard.realProgress') }}</span>
                            </div>
                        </div>
                        <table class="min-w-full">
                            <thead>
                                <tr class="bg-gray-50">
                                    <th
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <button
                                            v-if="sortBy === 'name'"
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortAsc = !sortAsc"
                                        >
                                            {{ $t('bundles.bundle') }}
                                            <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                            <icon-menu-down v-else width="16" height="16" />
                                        </button>
                                        <button
                                            v-else
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortBy = 'name'"
                                        >
                                            {{ $t('bundles.bundle') }}
                                        </button>
                                    </th>
                                    <th
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style="width: 30em"
                                    >
                                        <button
                                            v-if="sortBy === 'realProgress'"
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortAsc = !sortAsc"
                                        >
                                            {{ $t('dashboard.progress') }}
                                            <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                            <icon-menu-down v-else width="16" height="16" />
                                        </button>
                                        <button
                                            v-else
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortBy = 'realProgress'"
                                        >
                                            {{ $t('dashboard.progress') }}
                                        </button>
                                    </th>
                                    <th
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style="width: 4em"
                                    >
                                        <button
                                            v-if="sortBy === 'late'"
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortAsc = !sortAsc"
                                        >
                                            {{ $t('dashboard.delay') }}
                                            <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                            <icon-menu-down v-else width="16" height="16" />
                                        </button>
                                        <button
                                            v-else
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortBy = 'late'"
                                        >
                                            {{ $t('dashboard.delay') }}
                                        </button>
                                    </th>
                                    <th
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style="width: 4em"
                                    >
                                        <button
                                            v-if="sortBy === 'relativeLate'"
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortAsc = !sortAsc"
                                        >
                                            {{ $t('dashboard.relativeLate') }}
                                            <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                            <icon-menu-down v-else width="16" height="16" />
                                        </button>
                                        <button
                                            v-else
                                            class="hover:underline flex justify-center w-full"
                                            @click="sortBy = 'relativeLate'"
                                        >
                                            {{ $t('dashboard.relativeLate') }}
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="bundle in sortedBundleData" :key="id" class="hover:bg-gray-50">
                                    <td class="px-6 py-4 text-sm font-medium text-gray-800">
                                        {{ bundle.name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        <div class="flex flex-col gap-1 items-center">
                                            <div class="w-full flex gap-1 justify-between items-center">
                                                <div class="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        class="rounded-full h-2"
                                                        :style="{
                                                            width: `${bundle.referenceProgress}%`,
                                                            backgroundColor: 'rgb(247, 76, 20)',
                                                        }"
                                                    ></div>
                                                </div>
                                                <span class="text-2xs">{{ bundle.referenceProgress }}%</span>
                                            </div>
                                            <div class="w-full flex gap-1 justify-between items-center">
                                                <div class="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        class="rounded-full h-2"
                                                        :style="{
                                                            width: `${bundle.plannedProgress}%`,
                                                            backgroundColor: '#509cec',
                                                        }"
                                                    ></div>
                                                </div>
                                                <span class="text-2xs">{{ bundle.plannedProgress }}%</span>
                                            </div>
                                            <div class="w-full flex gap-1 justify-between items-center">
                                                <div class="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        class="rounded-full h-2"
                                                        :style="{
                                                            width: `${bundle.realProgress}%`,
                                                            backgroundColor: '#2a4365',
                                                        }"
                                                    ></div>
                                                </div>
                                                <span class="text-2xs">{{ bundle.realProgress }}%</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        <div class="flex justify-end">
                                            <span v-if="bundle.late > 0">+</span>
                                            {{ bundle.late }}j
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        <div class="flex justify-end">
                                            <span v-if="bundle.relativeLate > 0">+</span>
                                            {{ bundle.relativeLate }}%
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </app-dashboard-tiles>
        </div>
    </div>
</template>

<script>
import DashboardBurnDown from '@/features/dashboards/DashboardBurnDown';
import { getProject } from '@/features/projects/projects.service';
import AppDashboardHorizontalBar from '@/components/app-dashboard-horizontal-bar/AppDashboardHorizontalBar.vue';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { getPlannedTasks } from '@/features/tasks/plannedTasks.service';
import { getBundles } from '@/features/bundles/bundles.service';
import { sortBy } from '@/services/sanitize.service';
import IconChevronDown from '@/icons/IconChevronDown.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import { getReferences } from '@/features/planning/references/references.service';
import AppDashboardTiles from '@/components/app-dashboard-tiles/AppDashboardTiles.vue';

export default {
    components: {
        AppDashboardTiles,
        AppSelect,
        AppButton,
        IconChevronDown,
        AppDashboardHorizontalBar,
        DashboardBurnDown,
    },
    async created() {
        this.referenceSubject = new BehaviorSubject(
            localStorage.getItem('projectDashboard.referenceSelection.' + this.$route.params.projectId) || null,
        );
        this.subscriptions = [
            combineLatest([
                getPlannedTasks(this.$route.params.projectId, new BehaviorSubject(new Date()), this.referenceSubject),
                getProject(this.$route.params.projectId),
                getBundles(this.$route.params.projectId),
                getReferences(this.$route.params.projectId),
            ]).subscribe(([tasks, project, bundles, references]) => {
                this.tasks = tasks;
                this.project_EXE = project.projectFeatures.includes('project_EXE');
                this.bundles = bundles;
                this.references = references;
                const selectedReference = this.referenceSubject.getValue();
                if (selectedReference) {
                    this.referenceId = selectedReference.id || selectedReference;
                } else {
                    this.referenceId = references[0]?.id;
                }
            }),
        ];
    },
    data() {
        return {
            subscriptions: [],
            tasks: [],
            bundles: [],
            references: [],
            referenceId: null,
            referenceSubject: null,
            project_EXE: false,
            sortBy: 'name',
            sortAsc: true,
        };
    },
    watch: {
        referenceId(newVal) {
            this.referenceSubject.next(this.references.find(({ id }) => id === newVal));
        },
    },
    methods: {
        saveReferenceChoice() {
            localStorage.setItem(
                'projectDashboard.referenceSelection.' + this.$route.params.projectId,
                this.referenceId,
            );
        },
    },
    computed: {
        bundleProgressData() {
            const dataByBundles = sortBy(
                this.bundles,
                (bundle) => (bundle.reference ? bundle.reference + ' ' : '') + bundle.name,
            )
                .map((bundle) => {
                    let nbDaysDone = 0;
                    let nbDaysExpected = 0;
                    let nbDaysReference = 0;
                    let totalDaysReference = 0;
                    let totalDays = 0;

                    for (const task of this.tasks.filter((task) => task.service.bundleId === bundle.id)) {
                        totalDays += task.duration;
                        totalDaysReference += task.referenceDuration;
                        nbDaysExpected += (task.expectedProgress / 100) * task.duration;
                        nbDaysDone += (task.progress / 100) * task.duration;
                        nbDaysReference +=
                            task.referenceProgress && task.referenceDuration
                                ? (task.referenceProgress / 100) * task.referenceDuration
                                : 0;
                    }
                    if (totalDays === 0) {
                        return null;
                    }
                    return {
                        name: (bundle.reference ? bundle.reference + ' ' : '') + bundle.name,
                        realProgress: Math.round((nbDaysDone / totalDays) * 100),
                        plannedProgress: Math.round((nbDaysExpected / totalDays) * 100),
                        referenceProgress: Math.round((nbDaysReference / totalDaysReference) * 100),
                    };
                })
                .filter((e) => !!e);
            return {
                height: 200 + dataByBundles.length * 30 + 'px',
                labels: dataByBundles.map((item) => item.name),
                datasets: [
                    {
                        label: this.$t('dashboard.referenceProgress'),
                        data: dataByBundles.map((item) => item.referenceProgress),
                        backgroundColor: 'rgb(247, 76, 20)',
                    },
                    {
                        label: this.$t('dashboard.plannedProgress'),
                        data: dataByBundles.map((item) => item.realProgress),
                        backgroundColor: '#509cec',
                    },
                    {
                        label: this.$t('dashboard.realProgress'),
                        data: dataByBundles.map((item) => item.plannedProgress),
                        backgroundColor: '#2a4365',
                    },
                ],
            };
        },
        sortedBundleData() {
            const sorted = sortBy(this.bundleData, this.sortBy);
            if (!this.sortAsc) {
                sorted.reverse();
            }
            return sorted;
        },
        bundleData() {
            return this.bundles
                .map((bundle) => {
                    let nbDaysDone = 0;
                    let nbDaysExpected = 0;
                    let nbDaysReference = 0;
                    let totalDaysReference = 0;
                    let totalDays = 0;
                    let late = 0;

                    for (const task of this.tasks.filter((task) => task.service.bundleId === bundle.id)) {
                        totalDays += task.duration;
                        totalDaysReference += task.referenceDuration;
                        nbDaysExpected += (task.expectedProgress / 100) * task.duration;
                        nbDaysDone += (task.progress / 100) * task.duration;
                        nbDaysReference +=
                            task.referenceProgress && task.referenceDuration
                                ? (task.referenceProgress / 100) * task.referenceDuration
                                : 0;
                        late += task.late;
                    }
                    if (totalDays === 0) {
                        return null;
                    }
                    return {
                        id: bundle.id,
                        name: (bundle.reference ? bundle.reference + ' ' : '') + bundle.name,
                        realProgress: Math.round((nbDaysDone / totalDays) * 100),
                        plannedProgress: Math.round((nbDaysExpected / totalDays) * 100),
                        referenceProgress: Math.round((nbDaysReference / totalDaysReference) * 100),
                        late,
                        relativeLate: Math.round((late / totalDays) * 100),
                        totalDays,
                    };
                })
                .filter((e) => !!e);
        },
    },
};
</script>
